// The Section 8 Final Financial Addendum's Loan Amount — the amount the loan
// boards at (owner ruling 6, 2026-10-08).
//
// The rule is the server's (DealClosingService.LoanAmountProblem, applied by
// Create Loan, its preview and the addendum save): the amount must be above
// zero once rounded to the cent, with no business limit — only the datatypes'
// (owner ruling, 2026-10-09). This client copy only checks the input before it
// is sent; it sends the amount rounded to the cent it checked (half away from
// zero, as the server quantizes), never the raw typed digits, which a number
// could carry to a different cent. It displays the amount the server returns.
// The amount travels as a JavaScript
// number, which keeps every cent apart only below 2^46 dollars: above that,
// adjacent cents become the same number and the figure sent shifts by a cent
// (80000000000000.01 is sent as 80000000000000.02). So an amount above
// MAX_INPUT_CENTS — far below the server column's own limit — is refused here
// rather than sent rounded. Both are pinned to the same cases by
// tests/fixtures/addendum-loan-amount-cases.json.

export type LoanAmountInput =
  | { kind: "empty" }
  | { kind: "amount"; amount: number }
  | { kind: "invalid"; error: string };

export const LOAN_AMOUNT_NOT_A_NUMBER =
  "Enter the loan amount as a number, for example 1200000.00.";
export const LOAN_AMOUNT_NOT_POSITIVE = "Loan Amount must be greater than zero.";
export const LOAN_AMOUNT_TOO_LARGE_FOR_INPUT =
  "Loan Amount must be at most $70,368,744,177,663.99, the largest amount this form can enter exactly.";

/** The last cent below 2^46 dollars ($70,368,744,177,663.99). Below 2^46 the
 *  numbers a dollar amount can take are 1/128 of a dollar apart, finer than a
 *  cent, so every cent up to here reaches the server as typed; from 2^46 they
 *  are 1/64 apart and adjacent cents collide. Counting cents up to
 *  Number.MAX_SAFE_INTEGER instead let through amounts sent a cent off. */
const MAX_INPUT_CENTS = 2n ** 46n * 100n - 1n;

/** The typed amount in cents, rounded half away from zero as the server
 *  quantizes it. Exact: worked on the digits, never through a float. */
function centsOf(cleaned: string): bigint {
  const negative = cleaned.startsWith("-");
  const [whole, fraction = ""] = cleaned.replace(/^-/, "").split(".");
  const padded = fraction.padEnd(3, "0");
  let cents = BigInt(whole) * 100n + BigInt(padded.slice(0, 2));
  if (padded[2] >= "5") cents += 1n;
  return negative ? -cents : cents;
}

/** Positive cents as an exact two-decimal amount, e.g. 123456n → "1234.56". */
function decimalOfCents(cents: bigint): string {
  return `${cents / 100n}.${(cents % 100n).toString().padStart(2, "0")}`;
}

/** Reads the Loan Amount input. Thousands separators and a leading "$" are
 *  accepted; anything else that is not a plain number is refused. */
export function parseLoanAmountInput(text: string): LoanAmountInput {
  const trimmed = text.trim();
  if (trimmed === "") return { kind: "empty" };
  const cleaned = trimmed.replace(/^\$/, "").replace(/,/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) {
    return { kind: "invalid", error: LOAN_AMOUNT_NOT_A_NUMBER };
  }
  const cents = centsOf(cleaned);
  // Above zero once rounded to the cent, as the server quantizes it.
  if (cents < 1n) {
    return { kind: "invalid", error: LOAN_AMOUNT_NOT_POSITIVE };
  }
  if (cents > MAX_INPUT_CENTS) {
    return { kind: "invalid", error: LOAN_AMOUNT_TOO_LARGE_FOR_INPUT };
  }
  // The cent checked above, not Number(cleaned): a third decimal can round to
  // a different cent once the typed digits pass through a number.
  return { kind: "amount", amount: Number(decimalOfCents(cents)) };
}

/** The stored amount as the input shows it: always two decimals. */
export function loanAmountInputValue(amount: number | null | undefined): string {
  return amount == null ? "" : amount.toFixed(2);
}
